import {
	type ChangeEventHandler,
	type ReactNode,
	useRef,
	useState,
} from 'react';

import Button from 'components/Button';
import Text from 'components/Text';
import { useId, useValueChangeEffect } from 'hooks';
import type { HeadingLevel } from 'models/sitecore';
import { cn } from 'utils/classNames';
import { goToElement, selectTabbable } from 'utils/dom';
import { is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

import CheckboxListRow, { type Props as RowProps } from './CheckboxListRow';

export interface Props {
	className?: string;
	hasItemDividers?: boolean;
	headingLevel?: HeadingLevel;
	/** Number of items to show initially, enables expand/collapse if set. */
	initialVisibleItemsCount?: number;
	items: RowProps[];
	onCollapse?: () => void;
	onExpand?: () => void;
	onItemChange?: ChangeEventHandler<HTMLInputElement>;
	summary?: ReactNode;
	title?: string;
}

export default function CheckboxList({
	className,
	hasItemDividers,
	headingLevel = 'h2',
	initialVisibleItemsCount,
	items,
	onCollapse,
	onExpand,
	onItemChange,
	summary,
	title,
}: Props) {
	const { t } = useI18n();
	const itemsId = useId('checkbox-list');
	const itemsContainerRef = useRef<HTMLUListElement>(null);
	const isCollapsable = Boolean(
		initialVisibleItemsCount && initialVisibleItemsCount < items.length,
	);
	const [isExpanded, setIsExpanded] = useState<boolean>(!isCollapsable);
	const visibleItems = isExpanded
		? items
		: items.slice(0, initialVisibleItemsCount);

	// Focus the first 'new' item when the list is expanded.
	useValueChangeEffect(isExpanded, () => {
		if (!initialVisibleItemsCount) {
			return;
		}
		const firstNewItem =
			itemsContainerRef.current?.children[initialVisibleItemsCount];
		if (isExpanded && is.instance(firstNewItem, HTMLElement)) {
			const tabbables = selectTabbable(firstNewItem);
			if (tabbables[0]) {
				tabbables[0].focus();
			} else {
				goToElement(firstNewItem);
			}
		}
	});

	return (
		<div className={className}>
			{title && (
				<Text as={headingLevel} styleAs="p" className="mb-2 font-bold">
					{title}
				</Text>
			)}
			<div className="rounded-xs border border-subtle p-4">
				<ul
					id={itemsId}
					ref={itemsContainerRef}
					className={cn(
						'space-y-6',
						hasItemDividers &&
							'divide-y divide-subtle [&>:not(:last-child)]:pb-6',
					)}
				>
					{visibleItems.map((itemProps) => (
						<CheckboxListRow
							{...itemProps}
							key={itemProps.id}
							onChange={itemProps.onChange ?? onItemChange}
						/>
					))}
				</ul>

				{summary && (
					<div className="mt-4 border-t border-t-subtle pt-4">{summary}</div>
				)}
			</div>

			{isCollapsable && (
				<div className="mt-2 text-center">
					<Button
						variant="text"
						onClick={() => {
							setIsExpanded((current) => !current);
							if (isExpanded) {
								onCollapse?.();
							} else {
								onExpand?.();
							}
						}}
						aria-controls={itemsId}
						aria-expanded={isExpanded}
					>
						{isExpanded
							? t('general_show_fewer_items_button_text')
							: t('general_show_more_items_button_text')}
					</Button>
				</div>
			)}
		</div>
	);
}
CheckboxList.displayName = 'CheckboxList';
